// ============================================================
// LE BRUT AFFICHÉ EST CELUI QUI EST CHIFFRÉ.
// ------------------------------------------------------------
// Trouvé le 8 septembre 2026 en regardant l'écran du devis 2026-08-120 :
//
//     la carte « Géométrie déduite »  →  Volume brut  33,32 cm³
//     le prix, deux cartes plus bas   →              137,50 cm³
//
// Un facteur 4,1. Et par conséquent « Copeaux 4,13 cm³ · 12 % » là où
// l'enlèvement réel est de 108,3 cm³, soit 79 % — un ratio qui entre
// dans la signature de pièce et sert à retrouver les comparables.
//
// ── LA CAUSE : DEUX SOURCES POUR UNE MÊME GRANDEUR ──
//
// L'écran lisait `brut_variants[raw_shape]`, la variante PRÉCALCULÉE à
// la lecture du plan. Le prix, lui, lit les colonnes `raw_*` de la
// pièce. Les deux divergent dès que quelqu'un impose un brut — une
// consigne du deviseur (« je pars d'un brut de 50x50x55 »), une cote
// corrigée à la main. C'est justement le cas que le §2bis prévoit et
// qui décide du prix.
//
// Mesuré sur la base réelle : **8 pièces affichaient un volume de brut
// différent de celui qu'elles chiffraient.**
//
// Rien n'était en erreur : ni message, ni valeur aberrante, ni contrôle
// rouge. Un écran cohérent, des chiffres bien formatés, et faux. C'est
// le §11, treizième ligne, mot pour mot.
//
// ── LA RÈGLE ──
//
// **Le volume affiché VIENT du calcul de prix**, pas d'un second
// calcul : `computePiece` a déjà produit `detail.material.volume_cm3`,
// et c'est lui qu'on montre. Les cotes viennent des colonnes de la
// pièce, celles que le prix emploie et que l'écran laisse modifier.
//
// La variante précalculée garde son rôle — proposer les cotes quand on
// change de forme, sans aller-retour serveur (§2bis). Elle cesse
// seulement d'être une seconde source d'affichage.
// ============================================================

const nombre = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : null);
const round2 = (n) => Math.round(n * 100) / 100;

/** Les formes qui se commandent en barre — Ø et longueur débitée. */
export const RONDES = ['barre_ronde', 'barre_ronde_prismatique'];

/**
 * Les cotes du brut RETENUES sur la pièce.
 *
 * La convention est celle des champs de saisie : une forme ronde porte
 * `raw_diameter` et `raw_length`, les autres `raw_l / raw_w / raw_h`.
 */
export function cotesRetenues(piece = {}) {
  const ronde = RONDES.includes(piece.raw_shape);
  return {
    diametre_mm: ronde ? nombre(piece.raw_diameter) : null,
    longueur_mm: ronde ? nombre(piece.raw_length) : nombre(piece.raw_l),
    largeur_mm: ronde ? null : nombre(piece.raw_w),
    epaisseur_mm: ronde ? null : nombre(piece.raw_h),
  };
}

/** Deux jeux de cotes décrivent-ils le même brut ? */
export function memesCotes(a = {}, b = {}) {
  return ['diametre_mm', 'longueur_mm', 'largeur_mm', 'epaisseur_mm']
    .every((k) => nombre(a?.[k]) === nombre(b?.[k]));
}

/**
 * Les volumes à AFFICHER, tirés de ce qui est chiffré.
 *
 * @param {object} piece  la pièce, avec ses colonnes `raw_*`
 * @param {object} mat    `result.detail.material` — le calcul de PRIX
 * @param {object|null} base  `piece.geometrie`, l'objet d'affichage de la lecture
 * @returns {object|null}
 */
export function brutAffiche(piece = {}, mat = {}, base = null) {
  const variante = piece.brut_variants?.[piece.raw_shape] ?? null;
  const cotes = cotesRetenues(piece);

  // Le volume du PRIX d'abord. La variante ne sert que si le prix n'a
  // rien produit — une pièce sans matière, par exemple.
  const vBrut = nombre(mat?.volume_cm3) ?? nombre(variante?.volume_brut_cm3);
  if (vBrut == null) return base;

  const fini = nombre(piece.finished_volume);
  const volumePiece = fini != null && fini > 0 ? round2(fini) : (base?.volume_piece_cm3 ?? null);
  const copeaux = volumePiece != null ? round2(vBrut - volumePiece) : null;

  return {
    ...(base ?? {}),
    cotes_brut: cotes,
    // ── L'EXPLICATION NE SURVIT PAS À UNE COTE IMPOSÉE ──
    //
    // « Longueur et largeur + 2 × 2 mm par face… » décrit le calcul de
    // la VARIANTE. Si les cotes retenues n'en viennent plus — consigne
    // du deviseur, correction à la main — cette phrase explique un
    // chiffre qui n'est plus à l'écran. On la retire plutôt que de
    // laisser une justification juste pour un autre brut.
    cotes_brut_detail: memesCotes(variante?.cotes, cotes)
      ? (variante?.detail ?? base?.cotes_brut_detail ?? null)
      : null,
    volume_brut_cm3: vBrut,
    volume_piece_cm3: volumePiece,
    volume_copeaux_cm3: copeaux,
    ratio_enlevement: copeaux != null && vBrut > 0 ? copeaux / vBrut : null,
    volume_detail: base?.brut_shape === piece.raw_shape ? base.volume_detail : null,
    brut_shape: piece.raw_shape,
  };
}
